<template>
  <TransitionRoot as="template" :show="open">
    <Dialog class="fixed inset-0 z-[9999]" @close="$emit('close')">
      <TransitionChild
        as="div"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
        class="fixed inset-0 bg-gray-500/75 transition-opacity"
      />

      <div class="fixed inset-0 flex items-center justify-center p-4 sm:p-6">
        <TransitionChild
          as="div"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          class="flex items-center justify-center w-full"
        >
          <DialogPanel
            class="relative transform rounded-lg bg-white px-6 pb-6 pt-5 text-left shadow-xl transition-all w-full max-w-xl max-h-[90vh] overflow-y-auto"
          >
            <button
              type="button"
              class="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
              @click="$emit('close')"
            >
              <span class="sr-only">{{ $gettext("Fermer") }}</span>
              <XMarkIcon class="h-6 w-6" />
            </button>

            <DialogTitle class="text-base/7 font-semibold text-gray-900 mb-6">
              {{ $gettext("Ajouter un document") }}
            </DialogTitle>

            <form @submit.prevent="onSubmit" class="space-y-5">
              <!-- Fichier -->
              <div>
                <label class="block text-sm/6 font-medium text-gray-900 mb-2">{{ $gettext("Fichier du document") }}</label>
                <FileDropzone v-model="form.file" />
                <p v-if="formErrors.file" class="mt-1 text-sm text-red-600">{{ formErrors.file }}</p>
              </div>

              <!-- Nom -->
              <div>
                <label class="block text-sm/6 font-medium text-gray-900">{{ $gettext("Nom du document") }}</label>
                <div class="mt-2">
                  <input
                    v-model="form.name"
                    type="text"
                    :placeholder="$gettext('Ex: Politique LBC-FT')"
                    class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                  />
                </div>
                <p v-if="formErrors.name" class="mt-1 text-sm text-red-600">{{ formErrors.name }}</p>
              </div>

              <!-- Type + Catégorie -->
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm/6 font-medium text-gray-900">{{ $gettext("Type de document") }}</label>
                  <div class="mt-2">
                    <select
                      v-model="form.documentType"
                      class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                    >
                      <option value="">{{ $gettext("Sélectionner un type") }}</option>
                      <option v-for="option in documentTypeOptions" :key="option.value" :value="option.value">
                        {{ option.label }}
                      </option>
                    </select>
                  </div>
                  <p v-if="formErrors.documentType" class="mt-1 text-sm text-red-600">{{ formErrors.documentType }}</p>
                </div>
                <div>
                  <label class="block text-sm/6 font-medium text-gray-900">{{ $gettext("Catégorie") }}</label>
                  <div class="mt-2">
                    <select
                      v-model="form.category"
                      class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                    >
                      <option v-for="option in categoryOptions" :key="option.value" :value="option.value">
                        {{ option.label }}
                      </option>
                    </select>
                  </div>
                  <p v-if="formErrors.category" class="mt-1 text-sm text-red-600">{{ formErrors.category }}</p>
                </div>
              </div>

              <!-- Date de la dernière version -->
              <div>
                <label class="block text-sm/6 font-medium text-gray-900">{{ $gettext("Date de la dernière version") }}</label>
                <div class="mt-2">
                  <input
                    v-model="form.versionDate"
                    type="date"
                    class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:max-w-xs sm:text-sm/6"
                  />
                </div>
                <p v-if="formErrors.versionDate" class="mt-1 text-sm text-red-600">{{ formErrors.versionDate }}</p>
              </div>

              <!-- Fréquence de révision -->
              <fieldset>
                <legend class="block text-sm/6 font-medium text-gray-900">{{ $gettext("Fréquence de révision") }}</legend>
                <div class="mt-2 space-y-2">
                  <div v-for="option in revisionFrequencyOptions" :key="option.value" class="flex items-center gap-x-2">
                    <input
                      :id="`revision-frequency-${option.value}`"
                      v-model="form.revisionFrequency"
                      type="radio"
                      :value="option.value"
                      class="size-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    <label :for="`revision-frequency-${option.value}`" class="text-sm text-gray-900">
                      {{ option.value === "other" ? $gettext("Autre (préciser)") : option.label }}
                    </label>
                  </div>
                  <input
                    v-if="form.revisionFrequency === 'other'"
                    v-model="form.revisionFrequencyNote"
                    type="text"
                    :placeholder="$gettext('Ex: À chaque changement législatif')"
                    class="ml-6 block w-full max-w-md rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                  />
                </div>
                <p v-if="formErrors.revisionFrequency" class="mt-1 text-sm text-red-600">{{ formErrors.revisionFrequency }}</p>
              </fieldset>

              <p v-if="formErrors.base" class="text-sm text-red-600">{{ formErrors.base }}</p>

              <!-- Actions -->
              <div class="flex justify-end gap-x-3 pt-5 border-t border-gray-900/10">
                <button
                  type="button"
                  class="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                  @click="$emit('close')"
                >
                  {{ $gettext("Annuler") }}
                </button>
                <button
                  type="submit"
                  :disabled="saving"
                  class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                >
                  {{ saving ? $gettext("Ajout...") : $gettext("Ajouter") }}
                </button>
              </div>
            </form>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref, computed, reactive, watch } from "vue";
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import { useGettext } from "vue3-gettext";
import { useOrganizationDocumentStore } from "@/stores/organizationDocument";
import FileDropzone from "@/components/ui/FileDropzone.vue";
import { documentTypeLabels, categoryLabels, revisionFrequencyLabels, toOptions } from "./organizationDocumentLabels";

const { $gettext } = useGettext();

const props = defineProps({
  open: { type: Boolean, required: true },
});
const emit = defineEmits(["close", "created"]);

const organizationDocumentStore = useOrganizationDocumentStore();

const saving = ref(false);
const formErrors = reactive({});

const documentTypeOptions = computed(() => toOptions(documentTypeLabels($gettext)));
const categoryOptions = computed(() => toOptions(categoryLabels($gettext)));
const revisionFrequencyOptions = computed(() => toOptions(revisionFrequencyLabels($gettext)));

const today = () => new Date().toISOString().split("T")[0];

const form = reactive({
  file: null,
  name: "",
  documentType: "",
  category: "lbc_ft",
  versionDate: today(),
  revisionFrequency: "annually",
  revisionFrequencyNote: "",
});

watch(() => props.open, (val) => {
  if (!val) resetForm();
});

const resetForm = () => {
  form.file = null;
  form.name = "";
  form.documentType = "";
  form.category = "lbc_ft";
  form.versionDate = today();
  form.revisionFrequency = "annually";
  form.revisionFrequencyNote = "";
  Object.keys(formErrors).forEach((key) => delete formErrors[key]);
};

const onSubmit = async () => {
  Object.keys(formErrors).forEach((key) => delete formErrors[key]);

  if (!form.file) formErrors.file = $gettext("est obligatoire");
  if (!form.name) formErrors.name = $gettext("est obligatoire");
  if (!form.documentType) formErrors.documentType = $gettext("est obligatoire");
  if (!form.versionDate) formErrors.versionDate = $gettext("est obligatoire");
  if (!form.revisionFrequency) formErrors.revisionFrequency = $gettext("est obligatoire");
  if (form.revisionFrequency === "other" && !form.revisionFrequencyNote) formErrors.revisionFrequency = $gettext("veuillez préciser la fréquence");

  if (Object.keys(formErrors).length > 0) return;

  saving.value = true;

  const formData = new FormData();
  formData.append("document[file]", form.file);
  formData.append("document[name]", form.name);
  formData.append("document[document_type]", form.documentType);
  formData.append("document[category]", form.category);
  formData.append("document[version_date]", form.versionDate);
  formData.append("document[revision_frequency]", form.revisionFrequency);
  if (form.revisionFrequency === "other" && form.revisionFrequencyNote) {
    formData.append("document[revision_frequency_note]", form.revisionFrequencyNote);
  }

  await organizationDocumentStore.create(
    formData,
    (created) => {
      saving.value = false;
      resetForm();
      emit("created", created);
      emit("close");
    },
    (errors) => {
      saving.value = false;
      if (errors.value) {
        Object.assign(formErrors, errors.value);
      }
    }
  );
};
</script>
